import {Injectable} from '@angular/core';
import {RecordDataValue} from "../types/records-table.types";
import {DateComparison} from "../types/date-comparison.types";
import {REGEX} from "../constants/regex-constants";
import {NumberInputParams} from "../types/number-input.types";

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  public preventInvalidNumericInput(event: KeyboardEvent, isDecimalPoint?: boolean): void {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter'
    ];

    const isCtrlKey = event.ctrlKey || event.metaKey;
    const isAllowedCombination = (isCtrlKey && (
      event.code === 'KeyC' ||
      event.code === 'KeyX' ||
      event.code === 'KeyV' ||
      event.code === 'KeyA'
    ));

    const isNumberKey = /^[0-9]$/.test(event.key);
    const isDecimal = isDecimalPoint ? event.key === '.' || event.key === ',' : false;
    if (isDecimal) {
      this.preventMultipleDecimalSeparators(event);
      return;
    }

    if (!allowedKeys.includes(event.key) && !isNumberKey && !isAllowedCombination) {
      event.preventDefault();
    }
  }

  public allowPaste(event: Event): string | null {
    const inputElement = event.target as HTMLInputElement;
    const rawValue = inputElement.value;
    if (!rawValue) { return rawValue; }

    const normalizedValue = rawValue.replace(/,/g, '.');
    const filteredValue = REGEX.NUMBER_FORMAT_REGEX.test(normalizedValue);
    if (filteredValue) { return normalizedValue; }

    event.preventDefault();
    inputElement.blur();
    return null;
  }

  public isInvalidNumberInput(numberInputParams: NumberInputParams): boolean {
    const { value, minValue = 0, maxValue = 100 } = numberInputParams;
    const numberValue = parseInt(value, 10);

    return numberValue < minValue || numberValue > maxValue;
  }

  public validateDateComponent(value: RecordDataValue, key: string): RecordDataValue {
    if (Array.isArray(value)) { return value; }

    const rules = validationRules[key];

    switch (key) {
      case 'week':
        return this.isWeekValid(Number(value), rules) ? value : null;
      case 'month':
        return this.isMonthValid(Number(value), rules) ? value : null;
      case 'year':
        return this.isYearValid(value!.toString()) ? value : null;
      default:
        return true;
    }
  }

  public compareDates(dateComparison: DateComparison): boolean {
    const { earlierDate, laterDate, checkFutureDates } = dateComparison;
    if (!earlierDate && !laterDate) { return true; }

    if (checkFutureDates && this.isAnyDateInFuture(earlierDate, laterDate)) {
      return false;
    }

    return this.compareValidDates(earlierDate, laterDate);
  }

  public processTimeInput(inputValue: string): string {
    inputValue = inputValue.replace(/[^\d:.]/g, '');
    inputValue = inputValue.replace(/\./g, ':');

    const colonIndex = inputValue.indexOf(':');
    if (colonIndex !== -1) {
      inputValue = inputValue.slice(0, colonIndex + 1) +
        inputValue.slice(colonIndex + 1).replace(/:/g, '');
    }

    if (colonIndex !== -1) {
      const parts = inputValue.split(':');
      if (parts[0].length > 2) {
        parts[0] = parts[0].slice(0, 2);
      }

      if (parts[0].length === 1){
        parts[0] = parts[0].slice(0, 2);
      }

      if (parts[1] && parts[1].length > 2) {
        parts[1] = parts[1].slice(0, 2);
      }

      inputValue = parts.join(':');
    } else if (inputValue.length > 2) {
      inputValue = inputValue.slice(0, 2);
    }

    return inputValue;
  }

  public isNumericInput(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;

    if ([8, 9, 13, 27, 46].includes(charCode) ||
      (event.ctrlKey && [65, 67, 86, 88].includes(charCode))) {
      return true;
    }

    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  private preventMultipleDecimalSeparators(event: KeyboardEvent): void {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;
    const hasDecimalSeparator = inputValue.includes('.') || inputValue.includes(',');
    if (!hasDecimalSeparator) { return; }

    event.preventDefault();
  }

  private isWeekValid(value: number, rules: any): boolean {
    return value >= rules.min && value <= rules.max;
  }

  private isMonthValid(value: number, rules: any): boolean {
    return value >= rules.min && value <= rules.max;
  }

  private isYearValid(value: string): boolean {
    const regex = /^\d{4}$/;
    return regex.test(value);
  }

  private isAnyDateInFuture(earlierDate?: string | null, laterDate?: string | null): boolean {
    const currentDate = new Date();
    return !!(
      (earlierDate && new Date(earlierDate) > currentDate) ||
      (laterDate && new Date(laterDate) > currentDate)
    );
  }

  private compareValidDates(earlierDate?: string | null, laterDate?: string | null): boolean {
    if (!earlierDate || !laterDate) { return true; }

    return new Date(earlierDate) <= new Date(laterDate);
  }
}

export const validationRules: { [key: string]: any } = {
  week: { min: 1, max: 53 },
  month: { min: 1, max: 12 }
};
